import {FmlxButton, FmlxIcon, FmlxTextBox} from 'fmlx-common-ui';
import PropTypes from 'prop-types';
import {useRef, useState} from 'react';

import style from './InputFile.module.scss';
import InputFileHint from './InputFileHint';

const setAccept = (fileTypes) => {
  if (Array.isArray(fileTypes)) {
    return fileTypes.reduce((curr, next) => `${curr.ext}, ${next.ext}`);
  }
  return `${fileTypes.ext}`;
};

function Filename(props) {
  const {id, name, onClear, showClearButton} = props;

  return (
    <div className={style.fileName}>
      <div className={style.label}>
        <FmlxTextBox readOnly placeholder="File name" value={name} />
      </div>
      <div>
        <div className={style.actionContainer}>
          {showClearButton && (
            <FmlxButton
              id={`${id}-clear-button`}
              type="basic"
              variant="plain"
              onlyIcon
              icon={<FmlxIcon name="CloseOutline" />}
              onClick={onClear}
              tooltipDescription="Clear File"
            />
          )}
        </div>
      </div>
    </div>
  );
}

Filename.propTypes = {
  name: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClear: PropTypes.func,
  showClearButton: PropTypes.bool,
};

Filename.defaultProps = {
  name: '',
  id: 'default',
  onClear: () => {},
  showClearButton: false,
};

function InputFile({
  id,
  fileName,
  fileTypes,
  allowAllTypes,
  label,
  showProgress,
  value,
  onChange,
  showClearButton,
  hint,
}) {
  const [selectedFile, setSelectedFile] = useState(value);
  const inputEl = useRef();

  const handleOnClickBrowse = async () => {
    inputEl.current.click();
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    onChange(null);
  };

  const onHandleChange = () => {
    setSelectedFile(inputEl.current.files[0]);
    onChange(inputEl.current.files[0]);
  };

  return (
    <div className={style.inputFileContainer}>
      <div>
        <input
          className={style.hiddenInput}
          type="file"
          accept={allowAllTypes ? '.*' : setAccept(fileTypes)}
          ref={inputEl}
          onChange={onHandleChange}
        />
        <FmlxButton
          id={`${id}-browse-button`}
          label={label}
          showProgress={showProgress}
          onClick={handleOnClickBrowse}
          type={FmlxButton.Type.BASIC}
          variant={FmlxButton.Variant.OUTLINE}
        />
      </div>
      <div>
        <Filename
          id={`${id}-filename`}
          name={fileName ?? selectedFile?.name}
          onClear={handleClearFile}
          showClearButton={showClearButton}
        />
        {hint.message && <InputFileHint caption={hint.message} variant={hint.variant} />}
      </div>
    </div>
  );
}

InputFile.propTypes = {
  allowAllTypes: PropTypes.bool,
  fileName: PropTypes.string,
  fileTypes: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  hint: PropTypes.shape({
    message: PropTypes.string,
    variant: PropTypes.string,
  }),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  onChange: PropTypes.func,
  showClearButton: PropTypes.bool,
  showProgress: PropTypes.bool,
  value: PropTypes.object,
};

InputFile.defaultProps = {
  allowAllTypes: false,
  fileName: undefined,
  fileTypes: undefined,
  hint: {
    message: '',
    variant: '',
  },
  id: 'input-file',
  label: 'BROWSE',
  onChange: () => {},
  showClearButton: false,
  showProgress: false,
  value: undefined,
};

export default InputFile;
